import { createFileRoute, Link } from "@tanstack/react-router";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";

export const Route = createFileRoute("/help/mcp")({
	component: Mcp,
});

export function Mcp() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<h1 className="text-2xl font-medium tracking-tight">MCP overview</h1>
				<p className="text-sm text-muted-foreground">
					Create content assets from any MCP-compatible AI assistant.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground">What is MCP?</h2>
				<p>
					MCP (Model Context Protocol) lets AI assistants like Claude, Cursor,
					Windsurf, and Codex call external tools. AgentFramer exposes content
					creation tools through MCP, so your assistant can generate images,
					video, and audio without custom API work.
				</p>

				<h2 className="pt-2 text-base font-medium text-foreground">
					How it works
				</h2>
				<p>
					Connect AgentFramer to your MCP client once. After that, your
					assistant can pick models, start generation jobs, check status, fetch
					output URLs, and continue working with recent assets.
				</p>
				<p>
					MCP and the dashboard use the same workspace. Assets generated by an
					agent are stored in AgentFramer, visible in the dashboard, and billed
					against prepaid workspace balance.
				</p>

				<h2 className="pt-2 text-base font-medium text-foreground">
					What you can ask your AI to do
				</h2>
				<p>
					With MCP connected, your assistant can create and manage content
					assets in plain language. Examples:
				</p>
				<div className="space-y-2">
					<SetupCopyBlock
						text="Generate a product photo of a white sneaker on a clean background."
						multiline
					/>
					<SetupCopyBlock
						text="Create a 5 second 16:9 video of a perfume bottle turning on a pedestal."
						multiline
					/>
					<SetupCopyBlock
						text="Make a 30 second ambient audio loop for this campaign teaser."
						multiline
					/>
					<SetupCopyBlock
						text="Show me recent generations and upscale the best image."
						multiline
					/>
					<SetupCopyBlock
						text="How much prepaid balance is left before we generate more assets?"
						multiline
					/>
				</div>

				<h2 className="pt-2 text-base font-medium text-foreground">
					Setting it up
				</h2>
				<ol className="list-decimal space-y-2 pl-5">
					<li>
						Run{" "}
						<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
							npx agentframer@latest init
						</code>{" "}
						or install AgentFramer from your MCP client.
					</li>
					<li>
						Sign in and authorize the connection when AgentFramer opens the
						browser.
					</li>
					<li>
						Add prepaid balance during onboarding or from workspace billing.
					</li>
					<li>
						Restart the MCP client if required. The AgentFramer tools appear in
						your assistant.
					</li>
				</ol>

				<h2 className="pt-2 text-base font-medium text-foreground">
					Workspaces and billing
				</h2>
				<p>
					Your MCP session is scoped to one workspace. Generation history, team
					access, and prepaid balance are all workspace-scoped, so agents can
					work inside the same project context as the dashboard.
				</p>
			</div>

			<div className="flex items-center gap-4 text-sm">
				<Link
					to="/help/roles"
					className="text-muted-foreground transition-colors hover:text-foreground"
				>
					&larr; Roles and permissions
				</Link>
				<Link
					to="/help/mcp-tools"
					className="ml-auto text-primary transition-colors hover:text-primary/80"
				>
					MCP tools reference &rarr;
				</Link>
			</div>
		</article>
	);
}
